import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { requireAppCheck } from '@jfw/angular/shared/app-check';
import { FeedbackDialogHost } from '@jfw/angular/shared/common-components/feedback';
import {
  FeedbackClientContext,
  FeedbackSubmitRequest,
  FeedbackSubmitResponse,
  FeedbackUserTypeEnum,
} from '@jfw/types';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { DealerUserService } from '../dealer-user/dealer-user.service';
import { DealerFeedbackContextService } from './dealer-feedback-context.service';

/**
 * EXT-6239: Dealer Portal's side of the shared feedback dialog. Submissions
 * land in the same platform `Feedback` collection and IT review list as
 * Platform Portal's. Dealer Portal has no "My feedback" page, so there's no
 * `openMyFeedback`.
 */
@Injectable({ providedIn: 'root' })
export class DealerFeedbackDialogHost implements FeedbackDialogHost {
  private readonly httpClient = inject(HttpClient);
  private readonly contextService = inject(DealerFeedbackContextService);

  public readonly userType = FeedbackUserTypeEnum.DealerUser;
  public readonly teamName = 'Dealer Portal team';
  public readonly user = toSignal(
    inject(DealerUserService).currentDealerUser$.pipe(
      map((dealerUser) =>
        dealerUser
          ? {
              name:
                dealerUser.displayName?.trim() ||
                `${dealerUser.firstName} ${dealerUser.lastName}`.trim(),
              email: dealerUser.email,
            }
          : null,
      ),
    ),
    { initialValue: null },
  );

  public snapshot(): FeedbackClientContext {
    return this.contextService.snapshot();
  }

  public submit(
    request: FeedbackSubmitRequest,
  ): Observable<FeedbackSubmitResponse> {
    return this.httpClient.post<FeedbackSubmitResponse>(
      environment.feedback_api_server_url,
      request,
      { ...requireAppCheck },
    );
  }
}
